/**
 * As chamadas da fila de erro de e-mail (`NR-141`).
 *
 * Tudo o que a tela sabe sobre `GET /falhas-de-email` e
 * `POST /falhas-de-email/reprocessar` passa por aqui.
 */

import { queryOptions } from '@tanstack/react-query';
import type {
  EmailFailuresReprocessResponse,
  EmailFailuresResponse,
  EmailFailuresTriageResponse,
} from '@anery/api-contracts';

import { requisitar } from '../../../shared/api/clienteHttp';
import { RAIZ_DAS_FALHAS_DE_EMAIL } from '../../../shared/falhasDeEmail/resumo';
import { mapearPaginaDeFalhas, mapearResultadoDaTriagem, mapearResultadoDoReprocesso } from './mapeadores';
import type { PaginaDeFalhas, ResultadoDaTriagem, ResultadoDoReprocesso } from './tipos';

/** Cabe no teto de `IEmailErroQueries.TamanhoMaximoDePagina` (100). */
export const TAMANHO_DE_PAGINA = 25;

/** Quantas falhas cada chamada tenta. Espelha `EmailFailuresEndpoints.ReprocessMaxItems` na API. */
export const LOTE_DO_REPROCESSO = 10;

/** Quantos e-mails a triagem atribui, no máximo, por chamada. Espelha `EmailFailuresEndpoints.TriageMaxItems` na API. */
export const LOTE_DA_TRIAGEM = 500;

/** Os `409` do reprocesso e da triagem, que a tela distingue pelo `codigo`, não pelo status. */
export const CODIGO_REPROCESSO_EM_ANDAMENTO = 'REPROCESS_IN_PROGRESS';
export const CODIGO_COLETA_DESLIGADA = 'MAIL_COLLECTION_DISABLED';
export const CODIGO_TRIAGEM_EM_ANDAMENTO = 'TRIAGE_IN_PROGRESS';
export const CODIGO_CAIXA_SEM_ROTA = 'MAILBOX_NOT_ROUTED';

export const chavesDasFalhasDeEmail = {
  // A mesma raiz do resumo que a fila de triagem lê: invalidá-la atualiza a faixa também.
  raiz: RAIZ_DAS_FALHAS_DE_EMAIL,
  lista: (pagina: number) => [...RAIZ_DAS_FALHAS_DE_EMAIL, 'lista', pagina] as const,
};

/** Uma página das falhas abertas. `pagina` é 1-based, como a API. */
export function opcoesDasFalhasDeEmail(pagina: number) {
  return queryOptions({
    queryKey: chavesDasFalhasDeEmail.lista(pagina),
    queryFn: ({ signal }): Promise<PaginaDeFalhas> =>
      requisitar<EmailFailuresResponse>(`/falhas-de-email?page=${String(pagina)}&pageSize=${String(TAMANHO_DE_PAGINA)}`, {
        sinal: signal,
      }).then(mapearPaginaDeFalhas),
  });
}

/**
 * Reprocessa um lote curto das falhas de COLETA. Cada chamada tenta poucos
 * itens e diz se sobrou fila — quem chama decide se repete.
 */
export function reprocessarFalhasDeEmail(sinal?: AbortSignal): Promise<ResultadoDoReprocesso> {
  return requisitar<EmailFailuresReprocessResponse>('/falhas-de-email/reprocessar', {
    method: 'POST',
    ...(sinal !== undefined ? { sinal } : {}),
  }).then(mapearResultadoDoReprocesso);
}

/**
 * Atribui a tickets os e-mails da CAIXA que ainda não viraram ticket, até um teto por chamada. Não refaz só
 * "esta falha": a rodada não aceita filtro, e as falhas são os e-mails que a rodada anterior não atribuiu.
 */
export function triarEmailsDaCaixa(sinal?: AbortSignal): Promise<ResultadoDaTriagem> {
  return requisitar<EmailFailuresTriageResponse>('/falhas-de-email/triar', {
    method: 'POST',
    ...(sinal !== undefined ? { sinal } : {}),
  }).then(mapearResultadoDaTriagem);
}
